import axios, { type AxiosInstance, type CreateAxiosDefaults } from "axios";

/**
 * Split-horizon base URL for the Techne back-office API.
 *
 * - Browser         -> NEXT_PUBLIC_BO_API (public, TLS, internet-routable)
 * - Server, runtime -> NEXT_PUBLIC_BO_API_CLIENT (internal cluster service, no
 *                      public hop, no TLS) when set, else the public URL
 * - Build time      -> NEXT_PUBLIC_BO_API (see the NEXT_PHASE check below)
 *
 * The internal host resolves ONLY inside the server container, so it must never
 * be used for a request issued by the browser or by `next build`.
 *
 * Resolved PER REQUEST (see createBoApiClient) because service modules have no
 * 'use client' and are therefore loaded into BOTH the server and client graphs.
 * Freezing the value at module load would break silently in production the
 * moment a service module moved between graphs. The SSR pass of a 'use client'
 * component also runs in Node, where the internal host IS reachable and faster.
 */

const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const resolveBoApiBaseUrl = (): string | undefined => {
  // Literal process.env member access only - required for Next.js inlining.
  const publicUrl = clean(process.env.NEXT_PUBLIC_BO_API);

  // Browser: only the public host is reachable, full stop.
  if (typeof window !== "undefined") {
    return publicUrl;
  }

  // Build time (`next build`) runs in Node but OUTSIDE the container network,
  // so the internal host does not resolve there. Static generation, sitemap
  // `generateSitemaps()` and `generateMetadata` all fetch during this phase and
  // would fail with ENOTFOUND. Only a running server is on the internal network.
  if (process.env.NEXT_PHASE === "phase-production-build") {
    return publicUrl;
  }

  // Server at request time: prefer the internal host, fall back so local dev
  // and any environment without the var configured keep working.
  return clean(process.env.NEXT_PUBLIC_BO_API_CLIENT) ?? publicUrl;
};

/**
 * Drop-in replacement for axios.create({ baseURL: process.env.NEXT_PUBLIC_BO_API }).
 * Per-service config (e.g. maxBodyLength) passes straight through.
 */
export const createBoApiClient = (config?: CreateAxiosDefaults): AxiosInstance => {
  // NOTE: no baseURL here on purpose - it must not be frozen at module load.
  const instance = axios.create(config);

  instance.interceptors.request.use((requestConfig) => {
    // Respect an explicit per-call baseURL override if a caller sets one.
    if (!requestConfig.baseURL) {
      requestConfig.baseURL = resolveBoApiBaseUrl();
    }
    return requestConfig;
  });

  return instance;
};
